import './DomainDetails.css';

import { Input, Form, Card, message, Button, Tabs, Checkbox, Tooltip } from 'antd';
import { useContext, useEffect, useState } from 'react';
import { UserRole, ContextStatus, TagCategory } from '@leedpulse-monorepo/enums';
import AuthenticationContext from '../../contexts/auth/AuthenticationContext';
import DomainEditionFormContext from '../../contexts/forms/domains/DomainEditionFormContext';
import {
  FormCard,
  FormContainer,
  FormSectionTitle,
  READ_ONLY_INPUT_STYLE,
} from '@leedpulse-monorepo/components';
import Domain from '../../../domain/models/Domain';
import { DEFAULT_PROMPT_TEMPLATE, DEFAULT_OPENING_MESSAGE } from '@leedpulse-monorepo/utils';
import DomainMotifsCatalogEditor from './DomainMotifsCatalogEditor';
import { DOMAINS_PAGE_PATH } from '../../pages/domains/DomainsPage';
import { useWindowSize } from '@leedpulse-monorepo/hooks';
import { PlusOutlined, InfoCircleOutlined, ScheduleOutlined, TagOutlined, RobotOutlined, QuestionCircleOutlined } from '@ant-design/icons';
import ScenariosList from '../@shared/ScenariosList';
import { SCENARIO_PAGE_PATH } from '../../pages/scenarios/ScenarioDetailPage';
import { useNavigate } from 'react-router-dom';
import DomainElementsList, { DomainElementType } from './DomainElementsList';
import TagsList from './TagsList';
import { FirestoreCollections } from '@leedpulse-monorepo/firebase-metadata';

interface DomainDetailsProps {
  domain: Domain;
}

const TAB_KEYS = {
  generalInformation: '1',
  scenarios: '2',
  tags: '3',
  aiReceptionist: '4'
}

const DomainDetails: React.FC<DomainDetailsProps> = ({ domain }) => {
  const navigate = useNavigate();
  const domainEditionFormContext = useContext(DomainEditionFormContext);
  const authContext = useContext(AuthenticationContext);

  const windowSize = useWindowSize();

  useEffect(() => {
    if (domainEditionFormContext.wipDomain === null) {
      domainEditionFormContext.initWithDomain(domain);
    }
  }, [domain, domainEditionFormContext]);

  useEffect(() => {
    switch (domainEditionFormContext.updateStatus) {
      case ContextStatus.SUCCESS:
        message.success('Mise à jour du domaine avec succès');
        break;
      case ContextStatus.ERROR:
        message.error('Erreur lors de la mise à jour du domaine');
        break;
      default:
        break;
    }
  }, [domainEditionFormContext.updateStatus]);

  useEffect(() => {
    switch (domainEditionFormContext.deleteStatus) {
      case ContextStatus.SUCCESS:
        message.success('Domaine supprimé avec succès');
        navigate(DOMAINS_PAGE_PATH);
        break;
      case ContextStatus.ERROR:
        message.error('Erreur lors de la suppression du domaine');
        break;
      default:
        break;
    }
  }, [domainEditionFormContext.deleteStatus, navigate]);

  const isBusy =
    domainEditionFormContext.updateStatus === ContextStatus.LOADING ||
    domainEditionFormContext.deleteStatus === ContextStatus.LOADING;

  const isReadOnly =
    authContext.authentication == null ||
    ![UserRole.owner, UserRole.admin].includes(
      authContext.authentication?.role
    );

  const wipDomain = domainEditionFormContext.wipDomain;

  const [activeTab, setActiveTab] = useState(TAB_KEYS.generalInformation);

  return wipDomain === null ? (
    <Card loading />
  ) : (
    <Tabs
      activeKey={activeTab}
      onChange={setActiveTab}
      items={[
        {
          key: TAB_KEYS.generalInformation,
          label: 'Informations générales',
          icon: <InfoCircleOutlined />,
          children: (
            <FormCard
              title={wipDomain.id}
              collectionName={FirestoreCollections.domains}
              onDeleted={
                isReadOnly ? undefined : () => domainEditionFormContext.submitDelete()
              }
              onUpdated={
                isReadOnly ? undefined : () => domainEditionFormContext.submitUpdate()
              }
              isUpdateLoading={isBusy}
              isDeleteLoading={isBusy}
              headerActions={
                isReadOnly ? undefined :
                  <Form.Item
                    key="archived"
                    style={{ marginBottom: 0 }}
                  >
                    <Checkbox
                      disabled={isBusy}
                      checked={wipDomain.archived}
                      onChange={(e) =>
                        domainEditionFormContext.onArchivedChange(e.target.checked)
                      }
                    >
                      Archivé
                    </Checkbox>
                    <Tooltip title="Un domaine archivé n'est plus proposé dans les listes de choix du back-office. Les abonnements qui le portent continuent de fonctionner">
                      <QuestionCircleOutlined />
                    </Tooltip>
                  </Form.Item>
              }
            >
              <Form layout="vertical" requiredMark={false}>
                <FormSectionTitle title="ℹ️ Informations générales" />
                <FormContainer oneColumnMax>
                  <Form.Item label="Description">
                    <Input.TextArea
                      showCount
                      maxLength={500}
                      placeholder="L'immobilier englobe toutes les activités liées à l'achat, la vente, la location et la gestion de biens immobiliers, ..."
                      disabled={isBusy}
                      readOnly={isReadOnly}
                      rows={6}
                      style={isReadOnly ? READ_ONLY_INPUT_STYLE : {}}
                      onChange={(e) =>
                        domainEditionFormContext.onDescriptionChange(e.target.value)
                      }
                      value={wipDomain.description}
                    />
                  </Form.Item>

                  <Form.Item label="Intitulés de poste">
                    <DomainElementsList
                      wipDomain={wipDomain}
                      domainEditionFormContext={domainEditionFormContext}
                      elementType={DomainElementType.positions}
                      onElementsChange={domainEditionFormContext.onPositionsChange}
                      newElementLabel="Nouvelle position"
                      newElementPlaceholder="Ex: Responsable des ventes"
                      elementTitle="Intitulé de poste"
                    />
                  </Form.Item>

                  <Form.Item label="Réseaux">
                    <DomainElementsList
                      wipDomain={wipDomain}
                      domainEditionFormContext={domainEditionFormContext}
                      elementType={DomainElementType.networks}
                      onElementsChange={domainEditionFormContext.onNetworksChange}
                      newElementLabel="Nouveau réseau"
                      newElementPlaceholder="Ex: Century 21"
                      elementTitle="Réseau"
                    />
                  </Form.Item>
                </FormContainer>
              </Form>
            </FormCard>
          ),
        },
        {
          key: TAB_KEYS.scenarios,
          label: 'Scénarios',
          icon: <ScheduleOutlined />,
          children: (
            <div className="list-container">
              <div className="list-container__controls">
                <h2><span role="img" aria-label="scénarios">🎞️</span> Scénarios</h2>
                <div className="scenario-actions">
                  <Button
                    type="primary"
                    onClick={() =>
                      navigate(
                        `${DOMAINS_PAGE_PATH}/${wipDomain.id}${SCENARIO_PAGE_PATH}/new`
                      )
                    }
                  >
                    {windowSize.width > 768 ? (
                      <>
                        <PlusOutlined style={{ marginRight: '8px' }} />
                        Nouveau scénario
                      </>
                    ) : (
                      <PlusOutlined />
                    )}
                  </Button>
                </div>
              </div>
              <ScenariosList domainId={wipDomain.id} />
            </div>
          ),
        },
        {
          key: TAB_KEYS.tags,
          label: 'Tags',
          icon: <TagOutlined />,
          children: (
            <div className="list-container">
              <TagsList domainId={wipDomain.id} filterCategories={[TagCategory.typology, TagCategory.heat, TagCategory.status]} defaultCategory={TagCategory.typology} allEditable />
            </div>
          ),
        },
        {
          key: TAB_KEYS.aiReceptionist,
          label: 'IA Réceptionniste',
          icon: <RobotOutlined />,
          children: (
            <FormCard
              title={wipDomain.id}
              onDeleted={
                isReadOnly ? undefined : () => domainEditionFormContext.submitDelete()
              }
              onUpdated={
                isReadOnly ? undefined : () => domainEditionFormContext.submitUpdate()
              }
              isUpdateLoading={isBusy}
              isDeleteLoading={isBusy}
            >
              <Form layout="vertical" requiredMark={false}>
                <FormSectionTitle title="🤖 Prompt IA réceptionniste" />
                <FormContainer oneColumnMax>
                  <Form.Item
                    label="Premier message"
                    tooltip="Pré-rempli avec le message par défaut. Modifiez-le pour personnaliser, ou videz le champ pour revenir au comportement par défaut. Placeholders disponibles : {{assistant_name}}, {{public_label}}, {{callback_label}}"
                  >
                    <Input.TextArea
                      disabled={isBusy}
                      readOnly={isReadOnly}
                      rows={3}
                      style={isReadOnly ? READ_ONLY_INPUT_STYLE : {}}
                      onChange={(e) =>
                        domainEditionFormContext.onAiReceptionistFirstMessageChange(e.target.value)
                      }
                      value={wipDomain.ai_receptionist?.first_message || DEFAULT_OPENING_MESSAGE}
                    />
                  </Form.Item>

                  <Form.Item
                    label="Prompt IA réceptionniste"
                    tooltip="Pré-rempli avec le template par défaut. Le ton et la personnalité de base viennent déjà de la fiche IA de l'abonnement (champs Ton, Profil, Présentation) : les modifier là-bas personnalise aussi cet appel. Utilisez ce champ pour surcharger le comportement spécifique à ce domaine, ou videz-le pour revenir au template par défaut. Placeholders disponibles : {{assistant_name}}, {{public_label}}, {{callback_label}}"
                  >
                    <Input.TextArea
                      disabled={isBusy}
                      readOnly={isReadOnly}
                      rows={16}
                      style={isReadOnly ? READ_ONLY_INPUT_STYLE : {}}
                      onChange={(e) =>
                        domainEditionFormContext.onAiReceptionistPromptChange(e.target.value)
                      }
                      value={wipDomain.ai_receptionist?.prompt || DEFAULT_PROMPT_TEMPLATE}
                    />
                  </Form.Item>
                </FormContainer>

                <FormSectionTitle title="🏷️ Motifs et activités" />
                <DomainMotifsCatalogEditor
                  activities={wipDomain.ai_receptionist?.activities ?? []}
                  motifs={wipDomain.ai_receptionist?.motifs ?? []}
                  onActivitiesChange={domainEditionFormContext.onActivitiesChange}
                  onMotifsChange={domainEditionFormContext.onMotifsChange}
                  disabled={isBusy || isReadOnly}
                />
              </Form>
            </FormCard>
          ),
        }
      ]}
    />
  );
};

export default DomainDetails;
